import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Landmark, RefreshCw, ShieldQuestion, Loader2, Send, History, Link2 } from 'lucide-react';
import { disputesAPI } from '../../services/api';
import { toast } from 'sonner';

// EFI não manda webhook de MED: a descoberta é por consulta + defesa automática.
export const EfiMedPanel = ({ onChanged }) => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await disputesAPI.medConfig();
      setCfg(r.data);
    } catch (e) {
      toast.error('Não foi possível ler a configuração do MED');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const salvar = async (patch) => {
    setBusy('save');
    try {
      const r = await disputesAPI.medConfigUpdate(patch);
      setCfg(r.data);
      toast.success('Configuração do MED salva');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao salvar');
    } finally { setBusy(''); }
  };

  const consultar = async () => {
    setBusy('sync');
    try {
      const r = await disputesAPI.medSync(3);
      const d = r.data || {};
      if (d.ok) {
        toast.success(`${d.found || 0} infração(ões) · ${d.new || 0} nova(s) · `
          + `${d.defended || 0} defesa(s) enviada(s)`);
      } else {
        toast.error(d.message || 'Consulta não concluída');
      }
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao consultar a EFI');
    } finally { setBusy(''); }
  };

  const historico = async () => {
    setBusy('hist-efi');
    try {
      const r = await disputesAPI.medImportHistory(365);
      const d = r.data || {};
      if (d.ok) {
        const porStatus = Object.entries(d.by_status || {})
          .map(([k, v]) => `${v} ${k}`).join(' · ');
        toast.success(`${d.found || 0} encontrada(s) · ${d.new || 0} nova(s)`
          + (porStatus ? ` · ${porStatus}` : '')
          + (d.message ? `\n${d.message}` : ''), { duration: 16000 });
      } else {
        toast.error(d.message || 'Consulta não concluída', { duration: 10000 });
      }
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha na consulta');
    } finally { setBusy(''); }
  };

  const casarPedidos = async () => {
    setBusy('rematch');
    try {
      const r = await disputesAPI.medRematch();
      const d = r.data || {};
      toast.success(`${d.matched || 0} MED(s) casados com o pedido `
        + `(de ${d.total || 0} registrados)`, { duration: 8000 });
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao casar os pedidos');
    } finally { setBusy(''); }
  };

  if (!cfg) return null;

  return (
    <Card data-testid="efi-med-panel" className="border-blue-200 bg-blue-50/40">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Landmark className="h-5 w-5 text-blue-700" /> EFI · MED do Pix
          <Badge variant="outline" className="ml-1" data-testid="efi-med-env">
            {cfg.environment}
          </Badge>
          {!cfg.credentials_ok && (
            <Badge className="bg-amber-500 text-white">credenciais ausentes</Badge>
          )}
        </CardTitle>
        <CardDescription>
          A EFI não avisa por webhook: o sistema consulta as infrações a cada 10 minutos e,
          nas abertas, envia a defesa com as provas do pedido (crédito das moedas, data/hora,
          EndToEndId e dados do comprador). A importação de histórico é só leitura: registra
          o que já aconteceu sem bloquear ninguém.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Switch id="med-enabled" checked={cfg.enabled} disabled={busy === 'save'}
                    data-testid="efi-med-enabled"
                    onCheckedChange={(v) => salvar({ enabled: v })} />
            <Label htmlFor="med-enabled" className="text-sm">Consulta automática ligada</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="med-defense" checked={cfg.auto_defense} disabled={busy === 'save'}
                    data-testid="efi-med-auto-defense"
                    onCheckedChange={(v) => salvar({ auto_defense: v })} />
            <Label htmlFor="med-defense" className="text-sm">Defesa automática</Label>
          </div>
          <Button size="sm" variant="outline" onClick={consultar} disabled={busy === 'sync'}
                  data-testid="efi-med-sync">
            {busy === 'sync' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <RefreshCw className="h-4 w-4 mr-1" />}
            Consultar MED agora
          </Button>
          <Button size="sm" variant="ghost" onClick={historico}
                  disabled={busy === 'hist-efi'} data-testid="efi-med-import-history">
            {busy === 'hist-efi' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <History className="h-4 w-4 mr-1" />}
            Importar histórico (12 meses)
          </Button>
          <Button size="sm" variant="ghost" onClick={casarPedidos}
                  disabled={busy === 'rematch'} data-testid="efi-med-rematch">
            {busy === 'rematch' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <Link2 className="h-4 w-4 mr-1" />}
            Casar MEDs com os pedidos
          </Button>
        </div>
        {(cfg.last_sync || cfg.last_result) && (
          <p className="text-xs text-gray-600" data-testid="efi-med-last">
            Última consulta: {cfg.last_sync ? new Date(cfg.last_sync).toLocaleString('pt-BR') : '-'}
            {cfg.last_result ? ` · ${cfg.last_result}` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Bloco de defesa dentro do card de uma infração MED
export const MedDefenseBox = ({ dispute, onDone }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const abrir = async () => {
    setOpen(true);
    try {
      const r = await disputesAPI.defensePreview(dispute.id);
      setText(r.data?.text || '');
    } catch (e) {
      toast.error('Não foi possível montar a defesa');
    }
  };

  const enviar = async () => {
    setBusy(true);
    try {
      const r = await disputesAPI.defend(dispute.id, text);
      if (r.data?.ok) {
        toast.success('Defesa enviada à EFI');
        setOpen(false);
        if (onDone) onDone();
      } else {
        toast.error(r.data?.message || 'A EFI não aceitou a defesa');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao enviar a defesa');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-2 space-y-2"
         data-testid={`med-defense-${dispute.id}`}>
      <p className="text-xs text-gray-700 flex items-center gap-1.5">
        <ShieldQuestion className="h-3.5 w-3.5 text-blue-700" />
        {dispute.defense_sent_at
          ? `Defesa enviada em ${new Date(dispute.defense_sent_at).toLocaleString('pt-BR')}`
            + ` (${dispute.defense_actor || '-'})`
          : 'Defesa ainda não enviada'}
        {dispute.deadline ? ` · prazo da EFI: ${new Date(dispute.deadline).toLocaleString('pt-BR')}` : ''}
        {dispute.efi_status ? ` · status na EFI: ${dispute.efi_status}` : ''}
      </p>
      {!open ? (
        <Button size="sm" variant="outline" onClick={abrir}
                data-testid={`med-defense-open-${dispute.id}`}>
          <Send className="h-4 w-4 mr-1" />
          {dispute.defense_sent_at ? 'Ver / reenviar defesa' : 'Enviar defesa'}
        </Button>
      ) : (
        <div className="space-y-2">
          <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)}
                    data-testid={`med-defense-text-${dispute.id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={enviar} disabled={busy || text.trim().length < 20}
                    data-testid={`med-defense-send-${dispute.id}`}>
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                : <Send className="h-4 w-4 mr-1" />} Enviar à EFI
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EfiMedPanel;
